import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Lock, ArrowRight } from 'lucide-react';
import { TrustScoreRing } from './TrustScoreRing';
import { TrustBadgeComponent } from './TrustBadge';
import type { TrustScore, TrustBadge } from '../types';

interface TrustBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  trustScore: TrustScore;
  onVerify: (badgeId: string) => void;
}

export function TrustBreakdownModal({
  isOpen,
  onClose,
  trustScore,
  onVerify,
}: TrustBreakdownModalProps): JSX.Element | null {
  const [selectedBadge, setSelectedBadge] = useState<TrustBadge | null>(null);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        />

        {/* Modal */}
        <motion.div
          className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25 }}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-primary-500 to-primary-700 p-6 text-white">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4">
              <TrustScoreRing
                score={trustScore.overall}
                size="lg"
                showLabel={false}
              />
              <div>
                <h2 className="text-2xl font-bold">Trust Score</h2>
                <p className="text-primary-100">
                  {trustScore.level} Seller • {trustScore.badges.filter(b => b.earnedAt).length} badges earned
                </p>
              </div>
            </div>
          </div>

          {/* Breakdown */}
          <div className="p-6 space-y-6">
            {/* Score breakdown bars */}
            <div className="space-y-3">
              {Object.entries(trustScore.breakdown).map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="capitalize text-dark-600">{key}</span>
                    <span className="font-semibold text-dark-900">{value}%</span>
                  </div>
                  <div className="h-2 bg-dark-100 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-primary-500 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${value}%` }}
                      transition={{ duration: 1, delay: 0.2 }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Badges grid */}
            <div>
              <h3 className="text-sm font-semibold text-dark-900 mb-3 uppercase tracking-wider">
                Badges
              </h3>
              <div className="grid grid-cols-4 gap-3">
                {trustScore.badges.map((badge, index) => (
                  <motion.div
                    key={badge.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setSelectedBadge(badge)}
                  >
                    <TrustBadgeComponent
                      badge={badge}
                      size="md"
                      onClick={() => badge.earnedAt ? undefined : onVerify(badge.id)}
                    />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Next milestone */}
            {trustScore.nextMilestone && (
              <div className="bg-primary-50 rounded-xl p-4">
                <p className="text-sm text-primary-700">
                  <span className="font-semibold">Next milestone:</span>{' '}
                  {trustScore.nextMilestone.name}
                </p>
                <p className="text-xs text-primary-600 mt-1">
                  {trustScore.nextMilestone.pointsNeeded} more points needed
                </p>
              </div>
            )}
          </div>

          {/* Badge detail sheet */}
          <AnimatePresence>
            {selectedBadge && (
              <motion.div
                className="absolute inset-x-0 bottom-0 bg-white border-t border-dark-100 rounded-t-2xl p-6"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25 }}
              >
                <div className="flex items-start gap-4">
                  <TrustBadgeComponent badge={selectedBadge} size="lg" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-dark-900">{selectedBadge.name}</h4>
                    <p className="text-sm text-dark-500 mt-1">{selectedBadge.description}</p>

                    {selectedBadge.earnedAt ? (
                      <div className="flex items-center gap-2 mt-3 text-success">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">Earned on {selectedBadge.earnedAt.toLocaleDateString()}</span>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <div className="flex items-center gap-2 text-dark-500 mb-2">
                          <Lock className="w-4 h-4" />
                          <span className="text-sm">{selectedBadge.progress}% complete</span>
                        </div>
                        <div className="space-y-2">
                          {selectedBadge.requirements.map((req) => (
                            <div
                              key={req.id}
                              className={`flex items-center gap-2 text-sm ${
                                req.completed ? 'text-success' : 'text-dark-400'
                              }`}
                            >
                              {req.completed ? (
                                <CheckCircle className="w-4 h-4" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-dark-300" />
                              )}
                              <span>{req.description}</span>
                            </div>
                          ))}
                        </div>
                        {selectedBadge.requirements.some(r => !r.completed) && (
                          <button
                            onClick={() => onVerify(selectedBadge.id)}
                            className="mt-4 flex items-center gap-2 text-primary-600 font-medium text-sm hover:text-primary-700"
                          >
                            Complete now
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedBadge(null)}
                    className="p-1 hover:bg-dark-100 rounded-full"
                  >
                    <X className="w-5 h-5 text-dark-400" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
